import React from 'react';
import type { ComponentType } from 'react';

interface EmptyStateProps {
    /** Icon — accepts a ReactNode (e.g. <Icon />) or a ComponentType (e.g. Icon) */
    icon?: React.ReactNode | ComponentType<{ className?: string }>;
    title: string;
    description?: string;
    /** Arbitrary action slot (button, link, etc.) */
    action?: React.ReactNode;
    /** Convenience: render a CTA link button when provided alongside actionHref */
    actionLabel?: string;
    /** Convenience: href for the CTA link button */
    actionHref?: string;
    /** Color theme. Defaults to the dark dashboard theme. Use 'light' on
     *  customer-facing light surfaces (portal/public/sub) so the icon chip and
     *  text are legible against a white/gray-50 background. */
    theme?: 'dark' | 'light';
    className?: string;
}

export function EmptyState({
    icon,
    title,
    description,
    action,
    actionLabel,
    actionHref,
    theme = 'dark',
    className = '',
}: EmptyStateProps) {
    const isLight = theme === 'light';
    // Accepts either a rendered element (<Icon />) or a component (Icon). A
    // plain element passes isValidElement and renders as-is; a component —
    // function OR forwardRef (lucide icons are forwardRef objects, NOT
    // functions, so the old typeof==='function' check rendered them as an
    // invalid child) — goes through createElement.
    const renderedIcon = !icon
        ? null
        : React.isValidElement(icon)
            ? icon
            : React.createElement(icon as ComponentType<{ className?: string }>, { className: 'w-6 h-6' });

    return (
        <div className={`flex flex-col items-center justify-center py-16 px-4 text-center ${className}`}>
            {renderedIcon && (
                <div className={`w-12 h-12 rounded-xl border flex items-center justify-center mb-4 ${isLight ? 'bg-gray-100 border-gray-200 text-gray-400' : 'bg-bg-surface border-border-subtle text-zinc-500'}`}>
                    {renderedIcon}
                </div>
            )}
            <h3 className={`text-sm font-medium mb-1 text-balance ${isLight ? 'text-gray-700' : 'text-zinc-300'}`}>{title}</h3>
            {description && (
                <p className={`text-xs max-w-xs mb-4 text-pretty ${isLight ? 'text-gray-500' : 'text-zinc-500'}`}>{description}</p>
            )}
            {action && <div className="mt-2">{action}</div>}
            {!action && actionLabel && actionHref && (
                <a
                    href={actionHref}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-500 transition-colors"
                >
                    {actionLabel}
                </a>
            )}
        </div>
    );
}
